// =====================================================
// IMPORTS
// =====================================================

import { useEffect, useRef, useState } from "react";

import { DataTable } from "../components/DataTable";

import {
  AdvertisementBannerRequest,
  AdvertisementBannerResponse,
} from "../interfaces/AdvertisementBanner";

import { advertisementBannerService } from "../services/advertisementBannerService";

// =====================================================
// TYPE FIX
// =====================================================

type BannerTableRow =
  AdvertisementBannerResponse &
  Record<string, unknown>;

// =====================================================
// MODAL MODE
// =====================================================

type ModalMode =
  | "none"
  | "add"
  | "edit";

// =====================================================
// EMPTY FORM
// =====================================================

const emptyForm =
  (): AdvertisementBannerRequest => ({
    title: "",
    offerText: "",
    subText: "",
    ctaLabel: "Shop Now",
    ctaLink: "",
    imageFile: null,
    imageUrl: "",
    textPosition: "left",
    overlayOpacity: 40,
    textColor: "#ffffff",
    status: "Active",
    sortOrder: 0,
    placement: "Category",
  });

// =====================================================
// PREVIEW
// =====================================================

function BannerPreview({
  banner,
}: {
  banner: Partial<AdvertisementBannerRequest>;
}) {

  const posMap: Record<string, string> = {
    left: "flex-start",
    center: "center",
    right: "flex-end",
  };

  return (
    <div
      style={{
        position: "relative",
        borderRadius: 12,
        overflow: "hidden",
        height: 180,
        background: banner.imageUrl
          ? `url(${banner.imageUrl}) center/cover no-repeat`
          : "linear-gradient(135deg,#1e293b,#334155)",
        display: "flex",
        alignItems: "center",
        justifyContent:
          posMap[banner.textPosition || "left"] || "flex-start",
      }}
    >

      {/* OVERLAY */}

      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `rgba(0,0,0,${(banner.overlayOpacity ?? 40) / 100})`,
        }}
      />

      {/* CONTENT */}

      <div
        style={{
          position: "relative",
          zIndex: 1,
          padding: "20px 28px",
          color: banner.textColor || "#ffffff",
          textAlign:
            banner.textPosition === "center"
              ? "center"
              : banner.textPosition === "right"
              ? "right"
              : "left",
          maxWidth: "60%",
        }}
      >
        {banner.offerText && (
          <div
            style={{
              fontSize: 28,
              fontWeight: 900,
              lineHeight: 1.1,
              textShadow: "0 2px 8px rgba(0,0,0,.4)",
            }}
          >
            {banner.offerText}
          </div>
        )}

        {banner.subText && (
          <div
            style={{
              marginTop: 6,
              fontSize: 13,
              opacity: 0.9,
              textShadow: "0 1px 4px rgba(0,0,0,.4)",
            }}
          >
            {banner.subText}
          </div>
        )}

        {banner.ctaLabel && (
          <div
            style={{
              display: "inline-block",
              marginTop: 12,
              background: "#b45309",
              color: "#fff",
              padding: "6px 18px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {banner.ctaLabel}
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================
// DELETE CONFIRM MODAL
// =====================================================

function DeleteConfirmModal({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: 28,
          width: "100%",
          maxWidth: 400,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        }}
      >

        {/* ICON */}

        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            background: "#fee2e2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#dc2626"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </div>

        <h5
          className="fw-bold text-center mb-2"
          style={{ fontSize: 18 }}
        >
          Delete Banner?
        </h5>

        <p
          className="text-center text-muted mb-4"
          style={{ fontSize: 14 }}
        >
         Are you sure?
Once deleted, this banner cannot be recovered.
        </p>

        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary w-50"
            onClick={onCancel}
          >
            Cancel
          </button>

          <button
            className="btn btn-danger w-50"
            onClick={onConfirm}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// MODAL
// =====================================================

function BannerModal({
  mode,
  initial,
  onClose,
  onSave,
}: {
  mode: ModalMode;
  initial: AdvertisementBannerRequest;
  onClose: () => void;
  onSave: (data: AdvertisementBannerRequest) => void;
}) {

  const [form, setForm] =
    useState<AdvertisementBannerRequest>(initial);

  const imageRef = useRef<HTMLInputElement>(null);

  function f<K extends keyof AdvertisementBannerRequest>(
    key: K,
    value: AdvertisementBannerRequest[K]
  ) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleImageFile(
    e: React.ChangeEvent<HTMLInputElement>
  ) {
    const file = e.target.files?.[0];
    if (!file) return;

    f("imageFile", file);

    const reader = new FileReader();
    reader.onload = (ev) => {
      f("imageUrl", ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  }

  return (
    <div
      className="pm-del-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 9999,
        overflowY: "auto",
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "#fff",
          width: "100%",
          maxWidth: 700,
          borderRadius: 20,
          boxShadow: "0 24px 60px rgba(0,0,0,.2)",
          animation: "mIn .25s ease",
          margin: "0 auto",
          maxHeight: "95vh",
          overflowY: "auto",
        }}
      >

        {/* HEADER */}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 24px",
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <h5 style={{ margin: 0, fontWeight: 700 }}>
            {mode === "add" ? "🖼️ Add Advertisement Banner" : "Edit Banner"}
          </h5>
          <button className="btn-close" onClick={onClose} />
        </div>

        {/* BODY */}

        <div
          style={{
            padding: "22px 24px",
            maxHeight: "72vh",
            overflowY: "auto",
          }}
        >

          {/* PREVIEW */}

          <div style={{ marginBottom: 18 }}>
            <label
              className="form-label fw-semibold"
              style={{
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: ".5px",
                color: "#64748b",
              }}
            >
              Live Preview
            </label>
            <BannerPreview banner={form} />
          </div>

          {/* FORM */}

          <div className="row g-3">

            {/* TITLE */}

            <div className="col-12">
              <label className="form-label">Banner Title (Internal)</label>
              <input
                className="form-control"
                value={form.title}
                onChange={(e) => f("title", e.target.value)}
                placeholder="e.g. Diwali Sale Banner"
              />
            </div>

            {/* BANNER ADS PLACEMENT */}

            <div className="col-12">
              <label className="form-label">Banner Ads Placement</label>
              <select
                className="form-select"
                value={form.placement}
                onChange={(e) =>
                  f(
                    "placement",
                    e.target.value as
                      | "Category"
                      | "Top Trending Product"
                  )
                }
              >
                <option value="Category">Category</option>
                <option value="Top Trending Product">
                  Top Trending Product
                </option>
              </select>
            </div>

            {/* OFFER TEXT */}

            <div className="col-md-6">
              <label className="form-label">
                Offer Text{" "}
                <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                  (main headline)
                </span>
              </label>
              <input
                className="form-control"
                value={form.offerText}
                onChange={(e) => f("offerText", e.target.value)}
                placeholder="e.g. Save Up To 40% OFF"
              />
            </div>

            {/* SUB TEXT */}

            <div className="col-md-6">
              <label className="form-label">Sub Text</label>
              <input
                className="form-control"
                value={form.subText}
                onChange={(e) => f("subText", e.target.value)}
                placeholder="e.g. On All Jewellery Collections"
              />
            </div>

            {/* CTA LABEL */}

            <div className="col-md-6">
              <label className="form-label">CTA Button Label</label>
              <input
                className="form-control"
                value={form.ctaLabel}
                onChange={(e) => f("ctaLabel", e.target.value)}
                placeholder="e.g. Shop Now"
              />
            </div>

            {/* CTA LINK */}

            <div className="col-md-6">
              <label className="form-label">CTA Link</label>
              <input
                className="form-control"
                value={form.ctaLink}
                onChange={(e) => f("ctaLink", e.target.value)}
                placeholder="e.g. /shop.html"
              />
            </div>

            {/* IMAGE UPLOAD */}

            <div className="col-12">
              <label className="form-label">Banner Image</label>
              <div
                style={{
                  border: "2px dashed #e2e8f0",
                  borderRadius: 12,
                  overflow: "hidden",
                  background: "#f8fafc",
                }}
              >
                {form.imageUrl ? (
                  <div style={{ position: "relative" }}>
                    <img
                      src={
                        form.imageUrl.startsWith("data:")
                          ? form.imageUrl
                          : `${process.env.REACT_APP_API_PATH}/Uploads/${form.imageUrl}`
                      }
                      alt="banner"
                      style={{
                        width: "100%",
                        height: 130,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    <div
                      className="img-hover-overlay"
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(0,0,0,.45)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 10,
                        opacity: 0,
                      }}
                    >
                      <button
                        type="button"
                        style={{
                          background: "rgba(255,255,255,.92)",
                          border: "none",
                          borderRadius: 8,
                          padding: "6px 14px",
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                        onClick={() => imageRef.current?.click()}
                      >
                        Change
                      </button>
                      <button
                        type="button"
                        style={{
                          background: "rgba(255,255,255,.92)",
                          border: "none",
                          borderRadius: 8,
                          padding: "6px 14px",
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                          color: "#ef4444",
                        }}
                        onClick={() => {
                          f("imageUrl", "");
                          f("imageFile", null);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      height: 100,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      color: "#94a3b8",
                      cursor: "pointer",
                    }}
                    onClick={() => imageRef.current?.click()}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>
                      Click to upload banner image
                    </span>
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    padding: "10px 12px",
                    borderTop: "1px solid #f1f5f9",
                    background: "#fff",
                  }}
                >
                  <button
                    type="button"
                    style={{
                      background: "linear-gradient(135deg,#b45309,#d97706)",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      padding: "7px 14px",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => imageRef.current?.click()}
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Upload Image
                  </button>
                  <input
                    style={{
                      flex: 1,
                      padding: "7px 10px",
                      border: "1.5px solid #e2e8f0",
                      borderRadius: 8,
                      fontSize: 13,
                      outline: "none",
                    }}
                    placeholder="or paste image URL..."
                    value={
                      form.imageUrl?.startsWith("data:")
                        ? ""
                        : form.imageUrl || ""
                    }
                    onChange={(e) => f("imageUrl", e.target.value)}
                  />
                </div>
              </div>
              <input
                ref={imageRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageFile}
              />
            </div>

            {/* TEXT POSITION */}

            <div className="col-md-4">
              <label className="form-label">Text Position</label>
              <select
                className="form-select"
                value={form.textPosition}
                onChange={(e) =>
                  f(
                    "textPosition",
                    e.target.value as "left" | "center" | "right"
                  )
                }
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>

            {/* OVERLAY OPACITY — range slider */}

            <div className="col-md-4">
              <label className="form-label">
                Overlay Opacity ({form.overlayOpacity ?? 40}%)
              </label>
              <input
                type="range"
                className="form-range"
                min={0}
                max={85}
                value={form.overlayOpacity ?? 40}
                onChange={(e) =>
                  f("overlayOpacity", Number(e.target.value))
                }
              />
            </div>

            {/* TEXT COLOR — color picker + hex input */}

            <div className="col-md-4">
              <label className="form-label">Text Color</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="color"
                  style={{
                    width: 42,
                    height: 38,
                    border: "1.5px solid #e2e8f0",
                    borderRadius: 8,
                    padding: 2,
                    cursor: "pointer",
                  }}
                  value={form.textColor || "#ffffff"}
                  onChange={(e) => f("textColor", e.target.value)}
                />
                <input
                  className="form-control"
                  value={form.textColor || "#ffffff"}
                  onChange={(e) => f("textColor", e.target.value)}
                  style={{ fontFamily: "monospace" }}
                />
              </div>
            </div>

            {/* STATUS */}

            <div className="col-md-6">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={form.status}
                onChange={(e) =>
                  f("status", e.target.value as "Active" | "Hidden")
                }
              >
                <option value="Active">Active</option>
                <option value="Hidden">Hidden</option>
              </select>
            </div>

            {/* SORT ORDER */}

            <div className="col-md-6">
              <label className="form-label">Sort Order</label>
              <input
                type="number"
                className="form-control"
                min={0}
                value={form.sortOrder ?? 0}
                onChange={(e) =>
                  f("sortOrder", Number(e.target.value))
                }
              />
            </div>

          </div>
        </div>

        {/* FOOTER */}

        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <button
            className="btn btn-secondary"
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => onSave(form)}
            type="button"
            disabled={!form.title}
          >
            {mode === "add" ? "Add Banner" : "Update Banner"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes mIn { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
        .img-hover-overlay { transition: opacity .2s; }
        div:hover > .img-hover-overlay { opacity: 1 !important; }
      `}</style>
    </div>
  );
}

// =====================================================
// PAGE
// =====================================================

export function AddAdvertisementBannerPage() {

  const [banners, setBanners] =
    useState<AdvertisementBannerResponse[]>([]);

  const [loading, setLoading] = useState(false);

  const [message, setMessage] = useState("");

  const [modalMode, setModalMode] = useState<ModalMode>("none");

  const [editTarget, setEditTarget] =
    useState<AdvertisementBannerResponse | null>(null);

  const [deleteTargetId, setDeleteTargetId] =
    useState<number | null>(null);

  // =====================================================
  // LOAD
  // =====================================================

  async function loadBanners() {
    try {
      setLoading(true);
      const data = await advertisementBannerService.getAll();
      setBanners(data);
    } catch (error: any) {
      console.error(error);
      setMessage(error.message || "Failed to load banners");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadBanners();
  }, []);

  // =====================================================
  // OPEN ADD
  // =====================================================

  function openAdd() {
    setEditTarget(null);
    setModalMode("add");
  }

  // =====================================================
  // OPEN EDIT
  // =====================================================

  function openEdit(banner: AdvertisementBannerResponse) {
    setEditTarget(banner);
    setModalMode("edit");
  }

  // =====================================================
  // CLOSE
  // =====================================================

  function closeModal() {
    setModalMode("none");
    setEditTarget(null);
  }

  // =====================================================
  // SAVE
  // =====================================================

  async function handleSave(data: AdvertisementBannerRequest) {
    try {
      setMessage("");

      if (modalMode === "add") {
        const response =
          await advertisementBannerService.create(data);
        setMessage(response.message || "Banner added successfully");
      } else if (modalMode === "edit" && editTarget) {
        const response = await advertisementBannerService.update(
          editTarget.bannerID,
          data
        );
        setMessage(response.message || "Banner updated successfully");
      }

      await loadBanners();
      closeModal();
    } catch (error: any) {
      console.error(error);
      setMessage(error.message || "Something went wrong");
    }
  }

  // =====================================================
  // DELETE — open confirm modal
  // =====================================================

  function requestDelete(bannerID: number) {
    setDeleteTargetId(bannerID);
  }

  // =====================================================
  // DELETE — confirmed
  // =====================================================

  async function confirmDelete() {
    if (deleteTargetId === null) return;
    try {
      await advertisementBannerService.delete(deleteTargetId);
      setMessage("Banner deleted successfully");
      loadBanners();
    } catch (error: any) {
      console.error(error);
      setMessage(error.message || "Delete failed");
    } finally {
      setDeleteTargetId(null);
    }
  }

  // =====================================================
  // UI
  // =====================================================

  return (
    <>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold mb-0">Advertisement Banners</h4>
        <button className="btn btn-primary" onClick={openAdd}>
          Add Banner
        </button>
      </div>

      {/* MESSAGE */}

      {message && (
        <div
          className={`alert ${
            message.toLowerCase().includes("success") ||
            message.toLowerCase().includes("added") ||
            message.toLowerCase().includes("updated") ||
            message.toLowerCase().includes("deleted")
              ? "alert-success"
              : "alert-danger"
          }`}
        >
          {message}
        </div>
      )}

      {/* LOADING */}

      {loading && <div className="mb-3">Loading...</div>}

      <DataTable<BannerTableRow>
        title="All Advertisement Banners"
        rows={banners as BannerTableRow[]}
        rowKey={(r) => r.bannerID.toString()}
        searchPlaceholder="Search banner..."
        exportFileName="advertisement-banners"
        columns={[
          {
            key: "bannerID",
            label: "ID",
            render: (r: BannerTableRow) => r.bannerID,
          },

          {
            key: "title",
            label: "Title",
            render: (r: BannerTableRow) => (
              <span
                className="fw-semibold"
                style={{ whiteSpace: "nowrap" }}
              >
                {r.title || "-"}
              </span>
            ),
          },

          {
            key: "offerText",
            label: "Offer",
            render: (r: BannerTableRow) => (
              <span style={{ whiteSpace: "nowrap" }}>
                {r.offerText || "-"}
              </span>
            ),
          },

          {
            key: "subText",
            label: "Sub Text",
            render: (r: BannerTableRow) => (
              <span style={{ whiteSpace: "nowrap" }}>
                {r.subText || "-"}
              </span>
            ),
          },

          {
            key: "ctaLabel",
            label: "CTA Label",
            render: (r: BannerTableRow) => (
              <span style={{ whiteSpace: "nowrap" }}>
                {r.ctaLabel || "-"}
              </span>
            ),
          },

          {
            key: "ctaLink",
            label: "CTA Link",
            render: (r: BannerTableRow) => (
              <a
                href={r.ctaLink}
                target="_blank"
                rel="noreferrer"
                style={{ whiteSpace: "nowrap" }}
              >
                {r.ctaLink || "-"}
              </a>
            ),
          },

          {
            key: "imageUrl",
            label: "Image",
            render: (r: BannerTableRow) =>
              r.imageUrl ? (
                <img
                  src={`${process.env.REACT_APP_API_PATH}/Uploads/${r.imageUrl}`}
                  alt="banner"
                  style={{
                    width: 70,
                    height: 45,
                    objectFit: "cover",
                    borderRadius: 6,
                  }}
                />
              ) : (
                <span>No Image</span>
              ),
          },

          {
            key: "textPosition",
            label: "Position",
            render: (r: BannerTableRow) => (
              <span style={{ whiteSpace: "nowrap" }}>
                {r.textPosition}
              </span>
            ),
          },

          {
            key: "overlayOpacity",
            label: "Opacity",
            render: (r: BannerTableRow) => `${r.overlayOpacity}%`,
          },

          {
            key: "textColor",
            label: "Text Color",
            render: (r: BannerTableRow) => (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  whiteSpace: "nowrap",
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: r.textColor,
                    border: "1px solid #ccc",
                    flexShrink: 0,
                  }}
                />
                {r.textColor}
              </div>
            ),
          },

          {
            key: "placement",
            label: "Placement",
            render: (r: BannerTableRow) => (
              <span
                className="badge bg-primary"
                style={{
                  whiteSpace: "nowrap",
                  position: "relative",
                }}
              >
                {r.placement}
              </span>
            ),
          },

          {
            key: "status",
            label: "Status",
            render: (r: BannerTableRow) => (
              <span
                className={`badge ${
                  r.status === "Active" ? "bg-success" : "bg-secondary"
                }`}
                style={{
                  whiteSpace: "nowrap",
                  position: "relative",
                }}
              >
                {r.status}
              </span>
            ),
          },

          {
            key: "sortOrder",
            label: "Sort Order",
            render: (r: BannerTableRow) => r.sortOrder,
          },

          {
            key: "createdDate",
            label: "Created Date",
            render: (r: BannerTableRow) =>
              r.createdDate
                ? new Date(r.createdDate).toLocaleString()
                : "-",
          },

          {
            key: "actions",
            label: "Actions",
            className: "text-end",
            render: (row: BannerTableRow) => (
              <div
                className="d-flex justify-content-end gap-2"
                style={{ whiteSpace: "nowrap" }}
              >
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => openEdit(row)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => requestDelete(row.bannerID)}
                >
                  Delete
                </button>
              </div>
            ),
          },
        ]}
      />

      {/* BANNER MODAL */}

      {(modalMode === "add" || modalMode === "edit") && (
        <BannerModal
          mode={modalMode}
          initial={
            editTarget
              ? {
                  title: editTarget.title || "",
                  offerText: editTarget.offerText || "",
                  subText: editTarget.subText || "",
                  ctaLabel: editTarget.ctaLabel || "",
                  ctaLink: editTarget.ctaLink || "",
                  imageUrl: editTarget.imageUrl || "",
                  imageFile: null,
                  textPosition: editTarget.textPosition,
                  overlayOpacity: editTarget.overlayOpacity,
                  textColor: editTarget.textColor,
                  status: editTarget.status,
                  sortOrder: editTarget.sortOrder,
                  placement: editTarget.placement,
                }
              : emptyForm()
          }
          onClose={closeModal}
          onSave={handleSave}
        />
      )}

      {/* DELETE CONFIRM MODAL */}

      {deleteTargetId !== null && (
        <DeleteConfirmModal
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTargetId(null)}
        />
      )}
    </>
  );
}
